/** Printable document renderer (§4.6). One component per type, all reading only the
 *  frozen payload — never live data. Browser print is the PDF pipeline; print CSS
 *  owns the page breaks. Pink prints only as the walk-through's pen-fillable diamonds. */
import { useEffect, useState } from 'preact/hooks';
import { getRecord } from '../../core/db';
import { fmtCents, fmtQty } from '../../core/money';
import type { DocumentRecord, FrozenDoc, FrozenLine } from '../../core/types';
import { nav } from '../state';

const TYPE_LABEL: Record<DocumentRecord['type'], string> = {
  proposal: 'Proposal', progress_update: 'Progress update',
  change_order: 'Change order', walkthrough: 'Walk-through sheet',
};

export function DocView({ jobId, docId }: { jobId: string; docId: string }) {
  const [doc, setDoc] = useState<DocumentRecord | null | undefined>(undefined);
  useEffect(() => { void getRecord('documents', docId).then((d) => setDoc(d ?? null)); }, [docId]);

  if (doc === undefined) return <div class="muted">Loading document…</div>;
  if (doc === null || doc.jobId !== jobId) {
    return <div class="empty">That document does not exist on this job. <a href={`#/job/${jobId}`}>Back to the job.</a></div>;
  }
  const p = doc.payload;

  return (
    <div>
      <div class="noprint" style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px;">
        <div>
          <a href={`#/job/${jobId}`}>← Back to job</a>
          <span class="chip" style="margin-left:10px;">{TYPE_LABEL[doc.type]} · <span class="mono">{doc.number}</span></span>
          <span class={`chip ${doc.status === 'issued' ? 'chip-ok' : doc.status === 'superseded' ? 'chip-warn' : ''}`} style="margin-left:6px;">
            {doc.status === 'draft' ? 'Draft — not yet issued' : doc.status === 'issued' ? 'Issued — locked' : 'Superseded'}
          </span>
        </div>
        <button class="btn btn-primary" onClick={() => window.print()}>Print / save as PDF</button>
      </div>

      <div class="doc-paper">
        <DocHeader d={p} typeLabel={TYPE_LABEL[doc.type]} number={doc.number} status={doc.status} />
        {doc.type === 'proposal' && <ProposalBody d={p} />}
        {doc.type === 'change_order' && <ChangeOrderBody d={p} />}
        {doc.type === 'progress_update' && <ProgressBody d={p} />}
        {doc.type === 'walkthrough' && <WalkthroughBody d={p} />}
      </div>
    </div>
  );
}

function DocHeader({ d, typeLabel, number, status }: { d: FrozenDoc; typeLabel: string; number: string; status: string }) {
  return (
    <header class="doc-head">
      {(status === 'superseded' || status === 'draft') && (
        <div class="doc-stampband">
          {status === 'superseded'
            ? <div class="doc-watermark">SUPERSEDED</div>
            : <div class="doc-watermark doc-watermark-draft">DRAFT</div>}
        </div>
      )}
      <div class="doc-brandrow">
        <svg width="40" height="40" viewBox="0 0 44 44" aria-hidden="true">
          <g stroke="#476829" stroke-width="4.4" stroke-linecap="round" fill="none">
            <path d="M22 40 L22 10" /><path d="M22 40 C20 26 12 20 7 16" /><path d="M22 40 C24 26 32 20 37 16" />
          </g>
        </svg>
        <div class="doc-wordmark">{d.company.name.toUpperCase().includes('FOREVER GREEN') ? 'FOREVER GREEN' : d.company.name}
          <small>LANDSCAPE &amp; DESIGNS LLC{d.company.roc ? ` · ROC #${d.company.roc}` : ''}</small>
        </div>
        <div class="doc-meta mono">
          {typeLabel.toUpperCase()} {number}<br />
          Issued {d.issuedDate}<br />
          {d.validityDays > 0 ? <>Valid {d.validityDays} days<br /></> : null}
          {d.company.phone && <>{d.company.phone}<br /></>}
          {d.company.email}
        </div>
      </div>
      <div class="doc-rule" />
      <div class="doc-siterow">
        <div><b>{d.clientName || 'Client'}</b><br />{d.siteAddress}</div>
        <div class="mono small" style="text-align:right;">
          {d.parcel?.apn ? <>APN {d.parcel.apn}<br /></> : null}
          {d.parcel ? <>Lot {d.parcel.lotSqFt.toLocaleString('en-US')} sq ft</> : null}
        </div>
      </div>
    </header>
  );
}

function LinesTable({ lines, showVerify }: { lines: FrozenLine[]; showVerify?: boolean }) {
  return (
    <table class="doc-table">
      <thead>
        <tr>{showVerify && <th style="width:26px;"></th>}<th>Item</th><th class="money">Qty</th><th>Unit</th><th class="money">Rate</th><th class="money">Amount</th></tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={i}>
            {showVerify && <td><span class={`doc-tie ${l.verified ? 'doc-tie-v' : ''}`} /></td>}
            <td><b>{l.name}</b>{l.label && l.label !== l.name ? <div class="doc-sub">{l.label}</div> : null}</td>
            <td class="money num">{fmtQty(l.qty, l.unit)}</td>
            <td class="doc-sub">{l.unit}</td>
            <td class="money num">{fmtCents(l.rateCents)}</td>
            <td class="money num">{fmtCents(l.amountCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MathBlock({ d }: { d: FrozenDoc }) {
  const m = d.math;
  return (
    <div class="doc-math num">
      <div><span>Subtotal</span><span>{fmtCents(m.subtotalCents)}</span></div>
      {m.markupPct !== 0 && <div><span>Overhead {m.markupPct}%</span><span>{fmtCents(m.markupCents)}</span></div>}
      {m.adjustCents !== 0 && <div><span>Adjustment</span><span>{fmtCents(m.adjustCents)}</span></div>}
      {m.taxPct !== 0 && <div><span>Tax {m.taxPct}%</span><span>{fmtCents(m.taxCents)}</span></div>}
      <div class="doc-total"><span>Total</span><span>{fmtCents(m.totalCents)}</span></div>
      {m.depositPct > 0 && <div><span>Deposit to schedule ({m.depositPct}%)</span><span>{fmtCents(m.depositCents)}</span></div>}
    </div>
  );
}

function ProposalBody({ d }: { d: FrozenDoc }) {
  return (
    <>
      {d.narrative && <section class="doc-section"><h3>Scope of work</h3><p class="doc-prose">{d.narrative}</p></section>}
      <section class="doc-section">
        <h3>Pricing</h3>
        <LinesTable lines={d.lines} />
        <MathBlock d={d} />
      </section>
      <section class="doc-section doc-terms">
        <h3>Terms</h3>
        <p class="doc-prose small">{d.termsText}</p>
      </section>
      <section class="doc-section doc-signrow">
        <div><div class="doc-signline" />Accepted — {d.clientName || 'Client'}<span class="doc-sub"> · date</span></div>
        <div><div class="doc-signline" />{d.company.owner}, {d.company.name}</div>
      </section>
    </>
  );
}

function ChangeOrderBody({ d }: { d: FrozenDoc }) {
  return (
    <>
      <section class="doc-section">
        <p class="doc-prose">
          This change order amends proposal <b class="mono">{d.body?.['baseNumber']}</b>. Credits and revised
          lines below show exactly what changed; only the net difference is billed.
        </p>
        {d.body?.['note'] && <p class="doc-prose">{d.body['note']}</p>}
      </section>
      <section class="doc-section">
        <h3>Changes</h3>
        {d.lines.length === 0
          ? <p class="doc-prose">No differences from the base proposal at the time this was created.</p>
          : <><LinesTable lines={d.lines} /><MathBlock d={d} /></>}
      </section>
      <section class="doc-section doc-signrow">
        <div><div class="doc-signline" />Approved — {d.clientName || 'Client'}<span class="doc-sub"> · date</span></div>
        <div><div class="doc-signline" />{d.company.owner}, {d.company.name}</div>
      </section>
    </>
  );
}

function ProgressBody({ d }: { d: FrozenDoc }) {
  return (
    <>
      <section class="doc-section"><h3>Completed</h3><p class="doc-prose">{d.body?.['summary'] || '—'}</p></section>
      <section class="doc-section"><h3>Next</h3><p class="doc-prose">{d.body?.['next'] || '—'}</p></section>
      {d.body?.['notes'] && <section class="doc-section"><h3>Notes</h3><p class="doc-prose">{d.body['notes']}</p></section>}
      <section class="doc-section">
        <h3>Contract lines for reference</h3>
        <LinesTable lines={d.lines} />
      </section>
    </>
  );
}

function WalkthroughBody({ d }: { d: FrozenDoc }) {
  return (
    <>
      <p class="doc-prose doc-wt-hint">
        Tie off each line as you walk: fill the diamond when the quantity is right, correct it in the space if not.
        Every scanned or estimated number below is unverified until someone stands on the ground.
      </p>
      <section class="doc-section">
        <h3>Measured work — confirm or correct</h3>
        <table class="doc-table doc-wt">
          <thead><tr><th style="width:34px;">✓</th><th>Item</th><th class="money">Qty on bid</th><th style="width:130px;">Corrected</th><th style="width:200px;">Notes</th></tr></thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i}>
                <td><span class={`doc-tie doc-tie-lg ${l.verified ? 'doc-tie-v' : ''}`} /></td>
                <td><b>{l.name}</b>{l.label && l.label !== l.name ? <div class="doc-sub">{l.label}</div> : null}</td>
                <td class="money num">{fmtQty(l.qty, l.unit)} <span class="doc-sub">{l.unit}</span></td>
                <td><div class="doc-writeline" /></td>
                <td><div class="doc-writeline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {d.inventory && d.inventory.length > 0 && (
        <section class="doc-section">
          <h3>Site inventory — confirm counts and condition</h3>
          <table class="doc-table doc-wt">
            <thead><tr><th style="width:34px;">✓</th><th>Type</th><th>Species / detail</th><th class="money">Qty</th><th>Cond.</th><th style="width:180px;">Notes</th></tr></thead>
            <tbody>
              {d.inventory.map((iv, i) => (
                <tr key={i}>
                  <td><span class={`doc-tie doc-tie-lg ${iv.verified ? 'doc-tie-v' : ''}`} /></td>
                  <td><b>{iv.cat}</b></td>
                  <td>{iv.species || ''}{iv.notes ? <div class="doc-sub">{iv.notes}</div> : null}</td>
                  <td class="money num">{fmtQty(iv.qty, 'each')}</td>
                  <td>{iv.cond}</td>
                  <td><div class="doc-writeline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <section class="doc-section doc-signrow">
        <div><div class="doc-signline" />Walked by · date</div>
        <div class="doc-sub" style="align-self:flex-end;">{d.bidNumber} · {d.siteAddress}</div>
      </section>
    </>
  );
}

export function openDoc(jobId: string, docId: string): void {
  nav(`#/job/${jobId}/doc/${docId}`);
}
